import { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, Trash2, ArrowLeft, ArrowRight, Check, Cloud, CloudOff } from "lucide-react";
import { SystemChipsField } from "./SystemChipsField";
import { ContactChannelsField, hasInvalidChannels } from "./ContactChannelsField";
import { VoiceInputButton } from "@/components/VoiceInputButton";
import { FormStepper } from "@/components/common/FormStepper";

const STEPS = [
  { key: "base", label: "Stammdaten" },
  { key: "contact", label: "Kontakt" },
  { key: "systems", label: "Systeme & Zuständigkeit" },
  { key: "notes", label: "Notizen" },
];

const Req = () => <span className="text-red-500">*</span>;

function CustomerAutosaveIndicator({ state, time }) {
  if (!state || state === "idle") return null;
  const timeLabel = time ? time.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" }) : "";
  let content;
  if (state === "saving") {
    content = (<><Loader2 className="w-3.5 h-3.5 animate-spin" /><span>Wird gespeichert…</span></>);
  } else if (state === "offline") {
    content = (<><CloudOff className="w-3.5 h-3.5 text-amber-500" /><span>Lokal gesichert</span></>);
  } else {
    content = (<><Cloud className="w-3.5 h-3.5 text-emerald-500" /><span>Automatisch gespeichert{timeLabel ? ` · ${timeLabel}` : ""}</span></>);
  }
  return (
    <div className="flex items-center gap-1.5 text-xs text-muted-foreground bg-muted/60 border border-border rounded-full px-3 py-1" data-testid="customer-autosave-indicator">
      {content}
    </div>
  );
}

export const CustomerFormDialog = ({
  open,
  onOpenChange,
  editCustomer,
  formData,
  setFormData,
  categories,
  onSubmit,
  saving,
  draftRestored,
  onDiscardDraft,
  users = [],
  teams = [],
  autosaveState,
  autosaveTime,
}) => {
  const [step, setStep] = useState(0);
  const [maxReached, setMaxReached] = useState(0);
  const lastAdvanceRef = useRef(0);

  // Bei jedem Öffnen Schritt zurücksetzen; im Bearbeiten-/Entwurf-Modus alle Schritte freigeben.
  useEffect(() => {
    if (!open) return;
    setStep(0);
    setMaxReached(editCustomer || draftRestored ? STEPS.length - 1 : 0);
  }, [open, editCustomer, draftRestored]);

  const toggleSystem = (val) => setFormData((prev) => {
    const cur = prev.systems || [];
    return cur.includes(val) ? { ...prev, systems: cur.filter((s) => s !== val) } : { ...prev, systems: [...cur, val] };
  });

  const phonesInvalid = hasInvalidChannels(formData.phones, "phone");
  const emailsInvalid = hasInvalidChannels(formData.emails, "email");
  const invalid = phonesInvalid || emailsInvalid;

  // Validiert einen Schritt, liefert Fehlertext oder null.
  const validateStep = (idx) => {
    if (idx === 0 && !formData.company_name?.trim()) return "Bitte geben Sie einen Firmennamen ein";
    if (idx === 1 && (phonesInvalid || emailsInvalid)) return "Bitte korrigieren Sie die markierten Kontaktangaben";
    return null;
  };

  const goNext = () => {
    const err = validateStep(step);
    if (err) return; // Fehler werden inline markiert (required/aria)
    const next = Math.min(step + 1, STEPS.length - 1);
    lastAdvanceRef.current = Date.now();
    setStep(next);
    setMaxReached((m) => Math.max(m, next));
  };
  const goBack = () => setStep((s) => Math.max(0, s - 1));
  const goToStep = (idx) => { if (idx <= maxReached) setStep(idx); };

  const isLastStep = step === STEPS.length - 1;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isLastStep) { goNext(); return; }
    // Schutz gegen versehentliches Absenden direkt nach Erreichen des letzten Schritts
    // (Button 'Weiter' wird durch 'Anlegen' an gleicher Position ersetzt).
    if (Date.now() - lastAdvanceRef.current < 400) return;
    if (invalid || !formData.company_name?.trim()) {
      if (!formData.company_name?.trim()) setStep(0);
      else if (invalid) setStep(1);
      return;
    }
    onSubmit(e);
  };

  return (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="sm:max-w-[640px] max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <DialogTitle>{editCustomer ? "Kunde bearbeiten" : "Neuen Kunden anlegen"}</DialogTitle>
            <DialogDescription>Felder mit <Req /> sind Pflichtfelder.</DialogDescription>
          </div>
          {!editCustomer && <CustomerAutosaveIndicator state={autosaveState} time={autosaveTime} />}
        </div>
      </DialogHeader>

      {draftRestored && !editCustomer && (
        <div
          className="flex items-center justify-between gap-2 rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800 px-3 py-2 text-sm"
          data-testid="customer-draft-banner"
        >
          <span className="text-amber-800 dark:text-amber-200">Nicht gespeicherter Entwurf wiederhergestellt.</span>
          <Button type="button" variant="ghost" size="sm" onClick={onDiscardDraft} data-testid="customer-discard-draft-btn">
            <Trash2 className="w-4 h-4 mr-1" />
            Verwerfen
          </Button>
        </div>
      )}

      <div className="py-1">
        <FormStepper steps={STEPS} current={step} maxReached={maxReached} onStepClick={goToStep} />
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid gap-4 py-4 min-h-[260px]">
          {/* Schritt 1: Stammdaten */}
          {step === 0 && (
            <>
              <div className="space-y-2">
                <Label htmlFor="company_name">Firmenname <Req /></Label>
                <Input
                  id="company_name"
                  value={formData.company_name}
                  onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                  placeholder="Muster GmbH"
                  required
                  autoFocus
                  data-testid="customer-company-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="category">Kategorie</Label>
                  <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
                    <SelectTrigger data-testid="customer-category-select">
                      <SelectValue placeholder="Auswählen..." />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (<SelectItem key={cat} value={cat}>{cat}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="maintenance">Wartungsvertrag</Label>
                  <div className="flex items-center space-x-3 h-10 px-3 rounded-md border bg-background">
                    <Switch
                      id="maintenance"
                      checked={formData.has_maintenance_contract}
                      onCheckedChange={(checked) => setFormData({ ...formData, has_maintenance_contract: checked })}
                      data-testid="customer-maintenance-switch"
                    />
                    <Label htmlFor="maintenance" className="font-normal cursor-pointer">
                      {formData.has_maintenance_contract ? "Ja" : "Nein"}
                    </Label>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Schritt 2: Kontakt */}
          {step === 1 && (
            <>
              <div className="grid grid-cols-12 gap-2">
                <div className="col-span-5 space-y-2">
                  <Label htmlFor="address">Straße</Label>
                  <Input id="address" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} placeholder="Musterstraße 1" data-testid="customer-address-input" />
                </div>
                <div className="col-span-2 space-y-2">
                  <Label htmlFor="postal_code">PLZ</Label>
                  <Input id="postal_code" inputMode="numeric" value={formData.postal_code} onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })} placeholder="12345" data-testid="customer-postal-input" />
                </div>
                <div className="col-span-5 space-y-2">
                  <Label htmlFor="city">Stadt</Label>
                  <Input id="city" value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} placeholder="Musterstadt" data-testid="customer-city-input" />
                </div>
              </div>
              <ContactChannelsField
                type="phone"
                label="Telefonnummern"
                items={formData.phones || []}
                onChange={(v) => setFormData({ ...formData, phones: v })}
                testidPrefix="customer-phones"
              />
              <div className="space-y-2 max-w-[50%]">
                <Label htmlFor="phone_extension">Durchwahl (Standard)</Label>
                <Input id="phone_extension" type="text" inputMode="tel" value={formData.phone_extension || ""} onChange={(e) => setFormData({ ...formData, phone_extension: e.target.value })} placeholder="z.B. 123" data-testid="customer-phone-extension-input" />
              </div>
              <ContactChannelsField
                type="email"
                label="E-Mail-Adressen"
                items={formData.emails || []}
                onChange={(v) => setFormData({ ...formData, emails: v })}
                testidPrefix="customer-emails"
              />
            </>
          )}

          {/* Schritt 3: Systeme & Zuständigkeit */}
          {step === 2 && (
            <>
              <SystemChipsField value={formData.systems || []} onToggle={toggleSystem} onReplace={(systems) => setFormData((prev) => ({ ...prev, systems }))} />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="assigned_to">Zuständiger Mitarbeiter</Label>
                  <Select value={formData.assigned_to || "none"} onValueChange={(value) => setFormData({ ...formData, assigned_to: value === "none" ? "" : value })}>
                    <SelectTrigger data-testid="customer-assigned-select">
                      <SelectValue placeholder="Nicht zugewiesen" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nicht zugewiesen</SelectItem>
                      {users.map((u) => (<SelectItem key={u.id} value={u.email} data-testid={`customer-assigned-option-${u.id}`}>{u.full_name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="team_id">Team</Label>
                  <Select value={formData.team_id || "none"} onValueChange={(value) => setFormData({ ...formData, team_id: value === "none" ? "" : value })}>
                    <SelectTrigger data-testid="customer-team-select">
                      <SelectValue placeholder="Kein Team" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Kein Team</SelectItem>
                      {teams.map((t) => (<SelectItem key={t.id} value={t.id} data-testid={`customer-team-option-${t.id}`}>{t.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </>
          )}

          {/* Schritt 4: Notizen */}
          {step === 3 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="notes">Notizen</Label>
                <VoiceInputButton
                  label="Diktieren"
                  onTranscript={(t) => setFormData((prev) => ({ ...prev, notes: prev.notes ? `${prev.notes}\n${t}` : t }))}
                />
              </div>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Zusätzliche Informationen..."
                rows={5}
                data-testid="customer-notes-input"
              />
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          {step > 0 ? (
            <Button type="button" variant="outline" onClick={goBack} data-testid="customer-wizard-back-btn" className="mr-auto">
              <ArrowLeft className="w-4 h-4 mr-1.5" />
              Zurück
            </Button>
          ) : (
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="mr-auto">Abbrechen</Button>
          )}
          {!isLastStep ? (
            <Button type="button" onClick={goNext} data-testid="customer-wizard-next-btn" className="bg-[#1A3A5C] hover:bg-[#142E4A]">
              Weiter
              <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          ) : (
            <Button type="submit" disabled={saving || invalid} data-testid="customer-save-btn" className="bg-[#E8700A] hover:bg-[#C95F06]">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Check className="w-4 h-4 mr-1.5" />}
              {editCustomer ? "Speichern" : "Anlegen"}
            </Button>
          )}
        </DialogFooter>
      </form>
    </DialogContent>
  </Dialog>
  );
};
